import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ShieldAlert, Clock, Users, TrendingDown, Wallet } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/currency';

const money = (v) => formatCurrency(v || 0);

const ALERT_STYLES = {
  high_deduction: { cls: 'bg-red-100 text-red-700 border-red-200', Icon: ShieldAlert },
  installment_due: { cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: TrendingDown },
  advance_exceeds_salary: { cls: 'bg-orange-100 text-orange-700 border-orange-200', Icon: AlertTriangle },
  salary_pending: { cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Clock },
  salary_partial: { cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: Wallet },
};

export default function SalaryStatusTab() {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/salary-status?period=${p}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load salary status');
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  const s = data?.summary || {};
  const rows = data?.rows || [];

  const kpi = (label, value, Icon, cls) => (
    <Card className={cls} data-testid={`salary-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-white/60"><Icon size={18} /></div>
        <div>
          <div className="text-lg font-bold tabular-nums">{value}</div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="salary-status-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-bold">Salary Status &amp; Deduction Alerts</h3>
          <p className="text-xs text-muted-foreground">Loan/advance deductions and payment status for employees &amp; partners</p>
        </div>
        <Input
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="w-44"
          data-testid="salary-status-period"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpi('Headcount', s.headcount ?? 0, Users, 'border-slate-200')}
        {kpi('Gross Payroll', money(s.total_gross), Wallet, 'border-emerald-200 bg-emerald-50/40')}
        {kpi('Outstanding Loans', money(s.total_outstanding_loans), TrendingDown, 'border-amber-200 bg-amber-50/40')}
        {kpi('Net Payable', money(s.total_net_payable), Wallet, 'border-blue-200 bg-blue-50/40')}
      </div>

      {(s.with_alerts > 0) && (
        <div className="text-xs font-medium text-red-700 flex items-center gap-1.5" data-testid="salary-alerts-banner">
          <AlertTriangle size={14} /> {s.with_alerts} person(s) need attention · {s.salary_pending} salary pending · {s.high_deduction} high-deduction
        </div>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-right">Gross</th>
                <th className="px-3 py-2.5 text-right">Loan / Advance</th>
                <th className="px-3 py-2.5 text-right">Installment Due</th>
                <th className="px-3 py-2.5 text-right">Net Payable</th>
                <th className="px-3 py-2.5 text-left">Alerts</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No salaried staff found for this period.</td></tr>
              ) : rows.map((r) => (
                <tr key={`${r.type}-${r.id}`} className="border-t hover:bg-muted/40" data-testid={`salary-row-${r.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium flex items-center gap-2">
                      {r.name}
                      {r.type === 'partner' && <Badge variant="outline" className="text-[9px]">Partner</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{r.position || '—'}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{money(r.salary)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={r.loan_balance > 0 ? 'text-amber-700 font-medium' : ''}>{money(r.loan_balance)}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {r.installment_remaining > 0
                      ? <span className="text-red-600 font-medium">{money(r.installment_remaining)}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{money(r.net_payable)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {/* iter838 — Partial payment support. Show "Paid ✓" only
                          when fully settled; show a "Partial X/Y" chip when
                          some but not all of the monthly salary has been
                          paid; alerts list carries the salary_partial detail. */}
                      {r.salary_status === 'paid' && (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`salary-status-paid-${r.id}`}>Paid ✓</Badge>
                      )}
                      {r.salary_status === 'partial' && (
                        <Badge className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300" data-testid={`salary-status-partial-${r.id}`} title={`${money(r.salary_paid)} of ${money(r.salary)} paid — ${money(r.salary_remaining)} remaining`}>
                          Partial · {money(r.salary_paid)} / {money(r.salary)}
                        </Badge>
                      )}
                      {/* iter839 — Pay Remaining quick action. Opens
                          the appropriate detail page (Employees or
                          Partners) with query params to auto-open the
                          Pay dialog prefilled with the remaining amount
                          for this period. */}
                      {r.salary_status === 'partial' && (r.salary_remaining || 0) > 0.005 && (
                        <Link
                          to={`${r.type === 'partner' ? '/partners' : '/employees'}?payRemaining=1&${r.type === 'partner' ? 'partnerId' : 'empId'}=${r.id}&period=${period}&amount=${(r.salary_remaining || 0).toFixed(2)}`}
                          className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-amber-500 hover:bg-amber-600 text-white font-medium"
                          title={`Pay the remaining ${money(r.salary_remaining)}`}
                          data-testid={`pay-remaining-btn-${r.id}`}
                        >
                          Pay Rest
                        </Link>
                      )}
                      {(r.alerts || []).map((a, i) => {
                        const st = ALERT_STYLES[a.code] || ALERT_STYLES.salary_pending;
                        const Ic = st.Icon;
                        return (
                          <Badge key={i} className={`text-[10px] gap-1 border ${st.cls}`} title={a.msg} data-testid={`alert-${a.code}`}>
                            <Ic size={10} /> {a.msg}
                          </Badge>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
